/**
 * VatDataComparison
 * Displays a side-by-side comparison of database data vs VIES official data
 * for professional customers with VAT numbers
 */

import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  GlobeEuropeAfricaIcon
} from '@heroicons/react/24/outline';
import type { VatValidation } from '../../../types/facturation.types';

interface BillingContactData {
  name: string | null;
  address?: string | null;
  vat_number?: string | null;
  statut_juridique?: string | null;
  statut_tva?: string | null;
  email?: string | null;
}

const isLikelyValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

interface VatDataComparisonProps {
  billingContact: BillingContactData;
  vatValidationResult: VatValidation | null;
}

// Format VAT number for display
const formatVatNumber = (vatNumber: string): string => {
  const cleaned = vatNumber.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (cleaned.startsWith('BE') && cleaned.length === 12) {
    return `BE ${cleaned.slice(2, 6)}.${cleaned.slice(6, 9)}.${cleaned.slice(9)}`;
  }
  return vatNumber;
};

// Check if a value matches
const valuesMatch = (stored: string | null | undefined, official: string | null | undefined): boolean | null => {
  if (!stored || !official) return null;
  const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '').trim();
  const s1 = normalize(stored);
  const s2 = normalize(official);
  if (s1 === s2) return true;
  if (s1.includes(s2) || s2.includes(s1)) return true;
  return false;
};

export const VatDataComparison: React.FC<VatDataComparisonProps> = ({
  billingContact,
  vatValidationResult
}) => {
  const hasVatNumber = !!billingContact.vat_number;
  const hasViesData = vatValidationResult?.isValid && vatValidationResult.officialData;

  // If no VAT number, don't show anything
  if (!hasVatNumber) {
    return null;
  }

  // Calculate match status
  const nameMatch = hasViesData
    ? valuesMatch(billingContact.name, vatValidationResult.officialData?.name)
    : null;

  return (
    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
      <div className="flex items-center gap-2 mb-4">
        <BuildingOffice2Icon className="h-5 w-5 text-slate-600" />
        <h4 className="text-sm font-medium text-slate-700">
          Informations client professionnel
        </h4>
        {hasViesData && (
          <span className="ml-auto flex items-center gap-1 text-xs text-green-600">
            <CheckCircleIcon className="h-4 w-4" />
            VIES verifie
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Database Data Column */}
        <div className="bg-white rounded-lg p-3 border border-slate-200">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
            <BuildingOffice2Icon className="h-4 w-4 text-blue-500" />
            <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
              Base de donnees
            </span>
          </div>

          <div className="space-y-2 text-sm">
            {/* Name */}
            <div>
              <span className="text-xs text-slate-500">Nom:</span>
              <p className={`font-medium ${nameMatch === false ? 'text-amber-700' : 'text-slate-900'}`}>
                {billingContact.name || '-'}
              </p>
            </div>

            {/* VAT Number */}
            {billingContact.vat_number && (
              <div>
                <span className="text-xs text-slate-500">N° TVA:</span>
                <p className="font-mono text-slate-900">
                  {formatVatNumber(billingContact.vat_number)}
                </p>
              </div>
            )}

            {/* Legal Status */}
            {billingContact.statut_juridique && (
              <div>
                <span className="text-xs text-slate-500">Forme juridique:</span>
                <p className="text-slate-700">{billingContact.statut_juridique}</p>
              </div>
            )}

            {/* TVA Status */}
            {billingContact.statut_tva && (
              <div>
                <span className="text-xs text-slate-500">Statut TVA:</span>
                <p className="text-slate-700">{billingContact.statut_tva}</p>
              </div>
            )}

            {/* Address */}
            {billingContact.address && (
              <div>
                <span className="text-xs text-slate-500">Adresse:</span>
                <p className="text-slate-700 whitespace-pre-line">{billingContact.address}</p>
              </div>
            )}

            {/* Email — surfaced so users can spot a malformed billing email
                before submission (server rejects with VALIDATION_ERROR otherwise) */}
            <div>
              <span className="text-xs text-slate-500">Email facturation:</span>
              {billingContact.email ? (
                <p
                  className={`text-sm break-all ${
                    isLikelyValidEmail(billingContact.email) ? 'text-slate-700' : 'text-red-600 font-medium'
                  }`}
                >
                  {billingContact.email}
                  {!isLikelyValidEmail(billingContact.email) && (
                    <span className="ml-1 text-xs font-normal">(format invalide)</span>
                  )}
                </p>
              ) : (
                <p className="text-sm italic text-slate-400">Aucun email enregistre</p>
              )}
            </div>
          </div>
        </div>

        {/* VIES Data Column */}
        <div className={`rounded-lg p-3 border ${hasViesData ? 'bg-green-50 border-green-200' : 'bg-slate-100 border-slate-200'}`}>
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
            <GlobeEuropeAfricaIcon className={`h-4 w-4 ${hasViesData ? 'text-green-600' : 'text-slate-400'}`} />
            <span className={`text-xs font-medium uppercase tracking-wide ${hasViesData ? 'text-green-700' : 'text-slate-500'}`}>
              VIES (UE)
            </span>
          </div>

          {hasViesData ? (
            <div className="space-y-2 text-sm">
              {/* Official Name */}
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500">Nom officiel:</span>
                  {nameMatch !== null && (
                    nameMatch ? (
                      <CheckCircleIcon className="h-3 w-3 text-green-600" title="Correspond" />
                    ) : (
                      <ExclamationTriangleIcon className="h-3 w-3 text-amber-500" title="Difference detectee" />
                    )
                  )}
                </div>
                <p className={`font-medium ${nameMatch === false ? 'text-amber-700' : 'text-slate-900'}`}>
                  {vatValidationResult.officialData?.name || '-'}
                </p>
              </div>

              {/* Official Address */}
              {vatValidationResult.officialData?.address && (
                <div>
                  <span className="text-xs text-slate-500">Adresse officielle:</span>
                  <p className="text-slate-700 whitespace-pre-line">
                    {vatValidationResult.officialData.address}
                  </p>
                </div>
              )}

              {/* Parsed Address Components */}
              {(vatValidationResult.officialData?.street ||
                vatValidationResult.officialData?.postalCode ||
                vatValidationResult.officialData?.city) && (
                <div className="pt-2 mt-2 border-t border-green-200">
                  <span className="text-xs text-slate-500">Adresse structuree:</span>
                  <div className="text-slate-700">
                    {vatValidationResult.officialData.street && (
                      <p>{vatValidationResult.officialData.street}</p>
                    )}
                    {(vatValidationResult.officialData.postalCode || vatValidationResult.officialData.city) && (
                      <p>
                        {vatValidationResult.officialData.postalCode} {vatValidationResult.officialData.city}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Validation info */}
              <div className="pt-2 mt-2 border-t border-green-200">
                <span className="text-xs text-green-600">
                  {vatValidationResult.cached ? 'Donnees en cache' : 'Verification en temps reel'}
                  {vatValidationResult.requestDate && (
                    <span className="ml-1 text-slate-400">
                      ({new Date(vatValidationResult.requestDate).toLocaleDateString('fr-BE')})
                    </span>
                  )}
                </span>
              </div>
            </div>
          ) : vatValidationResult?.error ? (
            <div className="text-center py-4 text-red-500">
              <ExclamationTriangleIcon className="h-8 w-8 mx-auto mb-2 text-red-400" />
              <p className="text-xs font-medium">Verification VIES echouee</p>
              <p className="text-xs text-slate-500 mt-1">{vatValidationResult.error}</p>
            </div>
          ) : (
            <div className="text-center py-4 text-slate-500">
              <GlobeEuropeAfricaIcon className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-xs">En attente de verification...</p>
            </div>
          )}
        </div>
      </div>

      {/* Mismatch Warning */}
      {hasViesData && vatValidationResult.needsReview && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Differences detectees
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Les donnees de la base de donnees different des informations officielles VIES.
                Veuillez verifier et mettre a jour si necessaire.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VatDataComparison;
